import carsData from "./cars.json";

export type CarRow = {
  brand: string;
  category_code: number;
  category_name: string;
  model: string;
};

export type CarBrand = { id: string; name: string; priority?: number };
export type CarModel = { id: string; model: string };

const RAW = carsData as readonly CarRow[];

// Mirrors backend CarModelsLoader.carModelId — Firestore doc id pattern, so
// the IDs we surface here line up with what's stored on existing cars.
function modelDocId(row: CarRow): string {
  return `${row.category_code}_${row.model.replace(/\//g, "-")}`;
}

// Mirrors backend CarBrandsLoader display name override.
function brandDisplayName(brand: string): string {
  return brand === "VW" ? "Volkswagen" : brand;
}

function brandPriority(brand: string): number {
  return brand === "VW" ? 10 : 0;
}

let brandCache: CarBrand[] | null = null;
const modelCache = new Map<string, CarModel[]>();

export function getAllBrands(): CarBrand[] {
  if (brandCache) return brandCache;
  const seen = new Set<string>();
  const list: CarBrand[] = [];
  for (const row of RAW) {
    if (seen.has(row.brand)) continue;
    seen.add(row.brand);
    list.push({
      id: row.brand,
      name: brandDisplayName(row.brand),
      priority: brandPriority(row.brand),
    });
  }
  list.sort((a, b) => a.name.localeCompare(b.name, "ro"));
  brandCache = list;
  return list;
}

export function getModelsForBrand(brandId: string): CarModel[] {
  const cached = modelCache.get(brandId);
  if (cached) return cached;
  const seen = new Set<string>();
  const list: CarModel[] = [];
  for (const row of RAW) {
    if (row.brand !== brandId) continue;
    const key = row.model.trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    list.push({ id: modelDocId(row), model: row.model });
  }
  list.sort((a, b) => a.model.localeCompare(b.model, "ro"));
  modelCache.set(brandId, list);
  return list;
}

export function getBrandById(brandId: string): CarBrand | null {
  return getAllBrands().find((b) => b.id === brandId) ?? null;
}

export function getModelById(brandId: string, modelId: string): CarModel | null {
  return getModelsForBrand(brandId).find((m) => m.id === modelId) ?? null;
}
